import React, { useState } from 'react';
import { Box, Button, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import ScaleRoundedIcon from '@mui/icons-material/ScaleRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PDVThemeColors } from './PDVThemes';
import { useBalanca } from '../../shared/balanca/useBalanca';
import {
  VELOCIDADES_BALANCA, alterarModoBalanca, alterarVelocidadeBalanca, desconectarBalanca, escolherEConectarBalanca, lerConfigBalanca,
  TSituacaoPeso, TStatusBalanca,
} from '../../shared/balanca/balanca';

const TEXTO_STATUS: Record<TStatusBalanca, string> = {
  naoSuportado: 'Navegador sem suporte',
  desconectada: 'Desconectada',
  conectando: 'Conectando...',
  conectada: 'Conectada',
  erro: 'Erro',
};

const TEXTO_SITUACAO: Record<TSituacaoPeso, string> = {
  ok: '',
  instavel: 'Peso instável',
  negativo: 'Peso negativo — confira a tara',
  sobrecarga: 'Acima da capacidade',
  unidadeInvalida: 'Unidade da balança não suportada — configure em kg',
  semResposta: 'Sem resposta — confira a velocidade e o cabo',
};

export const formatarPeso = (peso: number | null) =>
  peso === null ? '—' : `${peso.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg`;

// Cartão "Balança" em Configurações do PDV — conecta a balança de checkout
// (porta COM pelo adaptador USB, ver shared/balanca/balanca.ts), escolhe a
// velocidade e mostra o peso ao vivo pra testar. Depois de conectada uma vez,
// o PDV reconecta sozinho nas próximas aberturas.
export const CartaoBalanca: React.FC<{ colors: PDVThemeColors }> = ({ colors }) => {
  const theme = useTheme();
  const { status, mensagemErro, leitura, modoBalanca, ultimoQuadro } = useBalanca();
  const [velocidade, setVelocidade] = useState(lerConfigBalanca().baudRate);

  const conectada = status === 'conectada';
  const cor = conectada ? '#16A34A' : status === 'erro' ? '#DC2626' : '#64748B';

  return (
    <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '12px', bgcolor: theme.palette.background.paper, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: '8px', bgcolor: alpha(colors.primarySolid, 0.12),
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.primarySolid, flexShrink: 0,
        }}>
          <ScaleRoundedIcon sx={{ fontSize: 17 }} />
        </Box>
        <Typography variant="subtitle2" fontWeight={700}>Balança</Typography>
        {/* Instruções de instalação num ícone (antes eram um parágrafo fixo
            no fim do cartão — saíram pra tela de configurações caber sem
            rolar). */}
        <Tooltip
          arrow
          title={
            <>
              Ao conectar, escolha a porta COM do adaptador USB da balança. A velocidade precisa ser a mesma configurada
              na balança (se o peso não aparecer, teste as outras). Nenhum outro programa pode estar usando a balança ao
              mesmo tempo. No IND560, configure a porta serial (COM1 ou COM2) com a atribuição &quot;Contínua&quot; (saída
              contínua padrão Toledo) e a unidade em kg.
            </>
          }
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help', ml: 'auto' }} />
        </Tooltip>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        O peso estável vai sozinho para a Quantidade (dá para digitar outro valor por cima). Toledo Prix e IND560.
      </Typography>

      {status === 'naoSuportado' ? (
        <Typography variant="body2" color="error">
          Este navegador não acessa balança. Use o Google Chrome ou o Microsoft Edge no computador, com o sistema aberto em HTTPS.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
            <Box sx={{
              px: 1.25, py: 0.5, borderRadius: 999, border: `1px solid ${alpha(cor, 0.6)}`, bgcolor: alpha(cor, 0.08),
            }}>
              <Typography variant="body2" fontWeight={700} sx={{ color: cor }}>{TEXTO_STATUS[status]}</Typography>
            </Box>
            {conectada && (
              <>
                <Typography variant="h6" fontWeight={700}>{formatarPeso(leitura.peso)}</Typography>
                <Typography variant="caption" color={leitura.situacao === 'ok' ? 'text.secondary' : 'warning.main'}>
                  {leitura.situacao === 'ok' ? (leitura.estavel ? 'Estável' : 'Estabilizando...') : TEXTO_SITUACAO[leitura.situacao]}
                </Typography>
              </>
            )}
          </Box>

          {conectada && ultimoQuadro && (
            // Diagnóstico: o que a balança mandou por último, como veio.
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontFamily: 'monospace', wordBreak: 'break-all' }}>
              Recebido: {ultimoQuadro}
            </Typography>
          )}

          {status === 'erro' && mensagemErro && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>{mensagemErro}</Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Velocidade</InputLabel>
              <Select
                label="Velocidade"
                value={velocidade}
                onChange={(evento) => {
                  const nova = Number(evento.target.value);
                  setVelocidade(nova);
                  alterarVelocidadeBalanca(nova);
                }}
              >
                {VELOCIDADES_BALANCA.map((v) => <MenuItem key={v} value={v}>{v} bps</MenuItem>)}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={() => escolherEConectarBalanca(velocidade)}
              disabled={status === 'conectando'}
              sx={{
                borderRadius: '10px',
                background: colors.primaryGradient,
                color: colors.textOnPrimary,
                '&:hover': { background: colors.hoverGradient },
              }}
            >
              {conectada ? 'Trocar porta' : 'Conectar balança'}
            </Button>

            {conectada && (
              <Button variant="outlined" color="inherit" onClick={() => desconectarBalanca()} sx={{ borderRadius: '10px' }}>
                Desconectar
              </Button>
            )}
          </Box>

          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
            <FormControlLabel
              control={<Switch checked={modoBalanca} onChange={(evento) => alterarModoBalanca(evento.target.checked)} />}
              label={<Typography variant="body2" fontWeight={700}>Modo balança</Typography>}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              A Quantidade fica sempre igual ao peso, inclusive 0 com a balança vazia. Para vender por unidade, digite a
              quantidade por cima.
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};
